import { useMemo } from 'react'
import dayjs from 'dayjs'
import { useRosterGetAll } from '../../lib/hooks/roster'
import { DutyStatus } from '../../../../api/src/shared'
import type { RosterSummary } from '../RosterCalendarView'

interface UseRosterKpiSummaryParams {
  /** 'YYYY-MM' (URL search param). Defaults to the current month. */
  selectedMonth?: string
  /** Optional crew filter. Defaults to company-wide (all crew). */
  crewMemberId?: string | null
  /** Optional duty-status filter. */
  dutyStatus?: DutyStatus | null
}

/**
 * Fetches the selected month's roster entries and returns the KPI summary used by
 * RosterKpiCard, counting DISTINCT CREW per duty status (matching CrewRoster). The
 * underlying query is month-scoped and dedupes with the Calendar/List views' fetch.
 */
export function useRosterKpiSummary({
  selectedMonth,
  crewMemberId = null,
  dutyStatus = null,
}: UseRosterKpiSummaryParams): RosterSummary {
  const month = selectedMonth ? dayjs(selectedMonth, 'YYYY-MM') : dayjs()
  const monthStart = month.startOf('month').format('YYYY-MM-DD')
  const monthEnd = month.endOf('month').format('YYYY-MM-DD')

  const { data: rosterEntries = [] } = useRosterGetAll({
    startDate: monthStart,
    endDate: monthEnd,
  })

  return useMemo(() => {
    const onDuty = new Set<string>()
    const offDuty = new Set<string>()
    const training = new Set<string>()
    const standby = new Set<string>()
    const leave = new Set<string>()
    const sick = new Set<string>()
    const positioning = new Set<string>()

    rosterEntries.forEach((entry: any) => {
      if (crewMemberId && entry.crewId !== crewMemberId) return
      if (dutyStatus && entry.dutyStatus !== dutyStatus) return

      switch (entry.dutyStatus) {
        case DutyStatus.OnDuty: onDuty.add(entry.crewId); break
        case DutyStatus.OffDuty: offDuty.add(entry.crewId); break
        case DutyStatus.Training: training.add(entry.crewId); break
        case DutyStatus.Standby: standby.add(entry.crewId); break
        case DutyStatus.Leave: leave.add(entry.crewId); break
        case DutyStatus.Sick: sick.add(entry.crewId); break
        case DutyStatus.Positioning: positioning.add(entry.crewId); break
      }
    })

    return {
      onDuty: onDuty.size,
      offDuty: offDuty.size,
      training: training.size,
      standby: standby.size,
      leave: leave.size,
      sick: sick.size,
      positioning: positioning.size,
    }
  }, [rosterEntries, crewMemberId, dutyStatus])
}
